import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { addLocale } from 'primereact/api';

import {
	dateFromLocalIso,
	formatOperationalClosureDateRange,
	getOperationalClosureDateBounds,
	validateOperationalClosure,
} from '../machineOperationalClosures';

const emptyDraft = () => ({ name: '', dates: null });
const OPERATIONAL_CLOSURE_CALENDAR_LOCALE = 'en-monday';

// Calendar reads its first weekday from the configured PrimeReact locale.
addLocale(OPERATIONAL_CLOSURE_CALENDAR_LOCALE, { firstDayOfWeek: 1 });

const MachineOperationalClosures = ({
	timezone,
	scheduleConfigured,
	closures,
	loading,
	loadError,
	onCreate,
	onUpdate,
	onDelete,
	onRetry,
}) => {
	const [draft, setDraft] = useState(emptyDraft);
	const [editingClosure, setEditingClosure] = useState(null);
	const [saving, setSaving] = useState(false);
	const [requestError, setRequestError] = useState('');
	const [showArchived, setShowArchived] = useState(false);
	const [touched, setTouched] = useState({ name: false, dates: false });
	const bounds = useMemo(() => getOperationalClosureDateBounds(timezone), [timezone]);
	const validation = useMemo(
		() => validateOperationalClosure(draft, bounds, closures, editingClosure?.closureId),
		[draft, bounds, closures, editingClosure],
	);
	const editableClosures = closures.filter((closure) => closure.status !== 'PAST');
	const archivedClosures = closures.filter((closure) => closure.status === 'PAST');
	const editing = Boolean(editingClosure);
	const nameError = touched.name ? validation.errors.name : '';
	const datesError = touched.dates ? validation.errors.dates : '';

	const resetEditor = () => {
		setDraft(emptyDraft());
		setEditingClosure(null);
		setRequestError('');
		setTouched({ name: false, dates: false });
	};

	const beginEdit = (closure) => {
		setEditingClosure(closure);
		setDraft({
			name: closure.name,
			dates: [dateFromLocalIso(closure.startsOnLocal), dateFromLocalIso(closure.endsOnLocal)],
		});
		setRequestError('');
		setTouched({ name: false, dates: false });
	};

	const submit = async () => {
		if (!validation.isValid || saving) return;
		setSaving(true);
		setRequestError('');
		try {
			if (editing) {
				await onUpdate(editingClosure.closureId, validation.payload);
			} else {
				await onCreate(validation.payload);
			}
			resetEditor();
		} catch (error) {
			setRequestError(error.message || 'The closure could not be saved. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const remove = async (closure) => {
		if (!closure.canDelete || saving || !window.confirm(`Remove “${closure.name}”?`)) return;
		setSaving(true);
		setRequestError('');
		try {
			await onDelete(closure.closureId);
			if (editingClosure?.closureId === closure.closureId) resetEditor();
		} catch (error) {
			setRequestError(error.message || 'The closure could not be removed. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const renderClosure = (closure, archived = false) => (
		<div key={closure.closureId} className={`machine-operational-closure-card ${archived ? 'machine-operational-closure-archived' : ''}`}>
			<div className="machine-operational-closure-details">
				<span className="text-sm font-medium text-800">{closure.name}</span>
				<span className="text-xs text-600">{formatOperationalClosureDateRange(closure.startsOnLocal, closure.endsOnLocal)}</span>
			</div>
			<div className="machine-operational-closure-actions">
				<span className={`machine-operational-closure-status machine-operational-closure-status-${closure.status?.toLowerCase()}`}>
					{closure.status === 'UPCOMING' ? 'Upcoming' : closure.status === 'ACTIVE' ? 'Active now' : 'Past'}
				</span>
				{closure.canEdit && (
					<Button type="button" icon="pi pi-pencil" aria-label={`Edit ${closure.name}`} className="p-button-text p-button-sm" onClick={() => beginEdit(closure)} />
				)}
				{closure.canDelete && (
					<Button type="button" icon="pi pi-trash" aria-label={`Remove ${closure.name}`} className="p-button-text p-button-danger p-button-sm" onClick={() => remove(closure)} />
				)}
			</div>
		</div>
	);

	return (
		<section className="machine-operational-closures mt-5 pt-4 border-top-1 surface-border">
			<div className="mb-3">
				<div>
					<h4 className="text-sm font-semibold text-700 m-0">Special closures</h4>
					<p className="text-xs text-500 mt-1 mb-0">Override normal opening hours for holidays, off-seasons, or other planned closures.</p>
				</div>
			</div>

			<Message
				severity="info"
				text="Choose a date or one continuous date range. Closures must begin after the machine’s local date, cannot overlap, and can be planned only through the end of next year."
				className="w-full mb-3"
			/>

			{!scheduleConfigured ? (
				<Message severity="warn" text="Save the normal schedule, including its timezone, before adding a special closure." className="w-full" />
			) : (
				<>
					<div className="machine-operational-closure-editor mb-3">
						<div className="grid">
							<div className="col-12 md:col-5">
								<label htmlFor="operational-closure-name" className="text-sm font-medium text-700 block mb-2">Closure name</label>
								<InputText
									id="operational-closure-name"
									value={draft.name}
									onChange={(event) => {
										const name = event.target.value;
										setDraft((current) => ({ ...current, name }));
										setTouched((current) => ({ ...current, name: true }));
									}}
									placeholder="e.g. Winter closure"
									maxLength={60}
									className={`w-full ${nameError ? 'p-invalid' : ''}`}
								/>
								{nameError && <small className="p-error">{nameError}</small>}
							</div>
							<div className="col-12 md:col-7">
								<label htmlFor="operational-closure-dates" className="text-sm font-medium text-700 block mb-2">Closed dates</label>
								<Calendar
									inputId="operational-closure-dates"
									value={draft.dates}
									onChange={(event) => {
										const dates = event.value;
										setDraft((current) => ({ ...current, dates }));
										setTouched((current) => ({ ...current, dates: true }));
									}}
									selectionMode="range"
									minDate={bounds.firstAllowedDate}
									maxDate={bounds.lastAllowedDate}
									readOnlyInput
									showIcon
									showButtonBar
									dateFormat="dd M yy"
									locale={OPERATIONAL_CLOSURE_CALENDAR_LOCALE}
									className={`w-full ${datesError ? 'p-invalid' : ''}`}
								/>
								<small className={datesError ? 'p-error' : 'text-xs text-500'}>
									{datesError || 'One selected day creates a one-day closure; selecting an end date includes every day in between.'}
								</small>
							</div>
						</div>
						{requestError && <Message severity="error" text={requestError} className="w-full mb-3" />}
						<div className="flex justify-content-end gap-2">
							{editing && <Button type="button" label="Cancel" className="p-button-text p-button-sm" onClick={resetEditor} disabled={saving} />}
							<Button type="button" label={editing ? 'Save closure' : 'Add closure'} icon="pi pi-check" className="p-button-sm" onClick={submit} disabled={!validation.isValid} loading={saving} />
						</div>
					</div>

					{loading ? (
						<div className="machine-operational-schedule-loading">Loading special closures…</div>
					) : loadError ? (
						<div className="machine-operational-schedule-load-error"><span>Special closures could not be loaded.</span><Button type="button" label="Retry" icon="pi pi-refresh" onClick={onRetry} className="p-button-sm" /></div>
					) : editableClosures.length ? (
						<div className="machine-operational-closure-list">{editableClosures.map((closure) => renderClosure(closure))}</div>
					) : (
						<p className="text-sm text-500 m-0">No current or upcoming special closures.</p>
					)}

					{archivedClosures.length > 0 && (
						<div className="machine-operational-closure-archive mt-4 pt-3 border-top-1 surface-border">
							<Button type="button" label={`Past closures (${archivedClosures.length})`} icon={showArchived ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={() => setShowArchived((current) => !current)} />
							{showArchived && <div className="machine-operational-closure-list mt-2">{archivedClosures.map((closure) => renderClosure(closure, true))}</div>}
						</div>
					)}
				</>
			)}
		</section>
	);
};

export default MachineOperationalClosures;
